import { apiClient } from './apiClient';
import type { PaginationInfo } from './projectsApi';
import type { Employee } from '@/types/timesheet';

export interface GetEmployeesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Comma separated when multiple departments are selected. */
  department?: string;
  role?: string;
  status?: string;
  /** Comma separated Person, Contractor and/or Asset. */
  type?: string;
}

export interface PaginatedEmployeesResponse {
  data: Employee[];
  pagination: PaginationInfo;
}

export interface EmployeeFacetCount {
  value: string;
  count: number;
}

export interface EmployeeDirectorySummary {
  totalEmployees: number;
  statuses: EmployeeFacetCount[];
  departments: EmployeeFacetCount[];
  roles: EmployeeFacetCount[];
  types: EmployeeFacetCount[];
}

export const employeesApi = {
  /**
   * Fetch a page of employees. Tolerates both the paged envelope and the legacy
   * plain array returned by GET /employees when no query parameters are sent.
   */
  async getEmployees(params?: GetEmployeesParams): Promise<PaginatedEmployeesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.department) queryParams.append('department', params.department);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.type) queryParams.append('type', params.type);

    const queryString = queryParams.toString();
    const url = queryString ? `/employees?${queryString}` : '/employees';
    const response = await apiClient.get<
      Employee[] | { data?: Employee[]; pagination?: PaginationInfo }
    >(url);

    const employees = Array.isArray(response) ? response : response.data ?? [];
    const pagination = (!Array.isArray(response) && response.pagination) || {
      page: params?.page || 1,
      pageSize: params?.pageSize || employees.length,
      totalItems: employees.length,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };

    return { data: employees, pagination };
  },

  /**
   * Organisation-wide employee counts and filter facets, so headline figures and
   * filter options are not limited to the employees on the current page.
   */
  async getDirectorySummary(): Promise<EmployeeDirectorySummary> {
    return apiClient.get<EmployeeDirectorySummary>('/employees/directory-summary');
  },
};
